import { useEffect, useRef, useState, useCallback } from 'react';

// Tiny silent MP3 (base64)
const SILENT_AUDIO = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTS1IAAAAgAAABTGFuIENsZW1lbnRlIC0gSmFja3kgUmVjb3JkaW5nc1RFTkMAAAAhAAABSwBpAHQAdABsAGUAIABKACAAbwBoAG4AbgBpAGUAVFhYXXX///////VT//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////';

export const useBackgroundAudio = () => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Initialize audio element once
    useEffect(() => {
        const audio = new Audio(SILENT_AUDIO);
        audio.loop = true;
        audio.volume = 0.01; // Not 0, as some browsers pause 0 volume
        audioRef.current = audio;

        return () => {
            audio.pause();
            audioRef.current = null;
        };
    }, []);

    const play = useCallback(async () => {
        if (!audioRef.current) return;
        try {
            await audioRef.current.play();
            setIsPlaying(true);
            setError(null);
            console.log('🔇 Background audio (silence) started');

            // Setup media session metadata
            if ('mediaSession' in navigator) {
                navigator.mediaSession.metadata = new MediaMetadata({
                    title: 'Background Tracking Active',
                    artist: 'K&C Logistics',
                    album: 'do not close',
                    artwork: [{ src: '/vite.svg', sizes: '96x96', type: 'image/svg+xml' }]
                });
                navigator.mediaSession.setActionHandler('play', () => audioRef.current?.play());
                navigator.mediaSession.setActionHandler('pause', () => audioRef.current?.pause());
            }
        } catch (err: any) {
            console.warn('Background audio blocked:', err);
            setIsPlaying(false);
            setError('Autoplay blocked. User interaction required.');
        }
    }, []);

    const stop = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
            console.log('🔇 Background audio stopped');
        }
    }, []);

    return { isPlaying, play, stop, error };
};
